import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  RotateCcw,
  CheckCircle,
  XCircle,
  Dumbbell,
  Gift,
  Settings,
  Users,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

// Ícone por categoria
const getCategoryIcon = (category) => {
  switch (category) {
    case 'workout': return <Dumbbell className="w-4 h-4" />;
    case 'bonus': return <Gift className="w-4 h-4" />;
    case 'system': return <Settings className="w-4 h-4" />;
    case 'community': return <Users className="w-4 h-4" />;
    default: return <Sparkles className="w-4 h-4" />;
  }
};

// Cor por categoria
const getCategoryColor = (category) => {
  switch (category) {
    case 'workout': return 'text-blue-400 bg-blue-500/20';
    case 'bonus': return 'text-amber-400 bg-amber-500/20';
    case 'system': return 'text-purple-400 bg-purple-500/20';
    case 'community': return 'text-green-400 bg-green-500/20';
    default: return 'text-zinc-400 bg-zinc-500/20';
  }
};

// Nome da categoria
const getCategoryName = (category) => {
  switch (category) {
    case 'workout': return 'Treino';
    case 'bonus': return 'Bônus';
    case 'system': return 'Sistema';
    case 'community': return 'Comunidade';
    default: return 'Geral';
  }
};

// Modal para criar/editar regra
const RuleModal = ({ rule, onClose, onSave, isEditing }) => {
  const [formData, setFormData] = useState({
    key: rule?.key || '',
    name: rule?.name || '',
    description: rule?.description || '',
    xp_value: rule?.xp_value || 0,
    category: rule?.category || 'general',
    is_active: rule?.is_active ?? true
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.key || !formData.name) {
      toast.error('Preencha o identificador e nome da regra');
      return;
    }
    
    if (formData.xp_value < 0) {
      toast.error('O valor de XP não pode ser negativo');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar regra');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-purple/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-neon-purple" />
            </div>
            <h3 className="font-bold text-white text-lg">
              {isEditing ? 'Editar Regra' : 'Nova Regra de XP'}
            </h3>
          </div>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Identificador (key) */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Identificador (único)</label>
            <Input
              value={formData.key}
              onChange={(e) => setFormData({...formData, key: e.target.value.toLowerCase().replace(/\s/g, '_')})}
              placeholder="ex: feedback_bonus"
              className="bg-zinc-800 border-zinc-700 text-white"
              disabled={isEditing && rule?.is_system}
              data-testid="rule-key-input"
            />
            {isEditing && rule?.is_system && (
              <p className="text-xs text-zinc-500 mt-1">Identificador de regras do sistema não pode ser alterado</p>
            )}
          </div>

          {/* Nome */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Nome da Regra</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="ex: Bônus por Feedback"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="rule-name-input"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Descrição (opcional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Descreva quando esta regra é aplicada..."
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3 min-h-[80px] resize-none"
              data-testid="rule-description-input"
            />
          </div>

          {/* Valor de XP */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Valor de XP</label>
            <Input
              type="number"
              min="0"
              step="1"
              value={formData.xp_value}
              onChange={(e) => setFormData({...formData, xp_value: parseFloat(e.target.value) || 0})}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="rule-xp-value-input"
            />
          </div>

          {/* Categoria */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Categoria</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3"
              data-testid="rule-category-select"
            >
              <option value="workout">Treino</option>
              <option value="bonus">Bônus</option>
              <option value="system">Sistema</option>
              <option value="community">Comunidade</option>
              <option value="general">Geral</option>
            </select>
          </div>

          {/* Status ativo */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFormData({...formData, is_active: !formData.is_active})}
              className={`w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-zinc-700'}`}
              data-testid="rule-active-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-sm text-zinc-400">
              {formData.is_active ? 'Regra ativa' : 'Regra desativada'}
            </span>
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1 bg-neon-purple hover:bg-neon-purple/80 text-white"
              data-testid="save-rule-btn"
            >
              {isSaving ? 'Salvando...' : <><Save className="w-4 h-4 mr-2" /> Salvar</>}
            </Button>
            <Button type="button" onClick={onClose} className="bg-zinc-800 text-white hover:bg-zinc-700">
              Cancelar
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Modal de confirmação de exclusão
const DeleteConfirmModal = ({ rule, onConfirm, onCancel, isDeleting }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-red-500/30 rounded-2xl p-6 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="font-bold text-white text-lg">Excluir Regra</h3>
        </div>
        
        <p className="text-zinc-300 mb-6">
          Tem certeza que deseja excluir a regra <strong>"{rule.name}"</strong>? 
          Esta ação não pode ser desfeita.
        </p>
        
        <div className="flex gap-3">
          <Button 
            onClick={onConfirm} 
            disabled={isDeleting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            data-testid="confirm-delete-rule-btn"
          >
            {isDeleting ? 'Excluindo...' : 'Sim, Excluir'}
          </Button>
          <Button 
            onClick={onCancel} 
            disabled={isDeleting}
            className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
          >
            Cancelar
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Card de regra
const RuleCard = ({ rule, onEdit, onDelete, onToggle }) => {
  const categoryColor = getCategoryColor(rule.category);
  
  return (
    <motion.div
      className={`bg-zinc-950/50 border rounded-xl p-4 ${rule.is_active ? 'border-white/5' : 'border-red-500/20 opacity-60'}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryColor}`}>
            {getCategoryIcon(rule.category)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-bold text-white">{rule.name}</h4>
              {rule.is_system && (
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Sistema</span>
              )}
              {!rule.is_active && (
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Desativada</span>
              )}
            </div>
            <p className="text-sm text-zinc-500 mb-2">{rule.description || 'Sem descrição'}</p>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span className={`px-2 py-0.5 rounded ${categoryColor}`}>
                {getCategoryName(rule.category)}
              </span>
              <span className="text-zinc-600">ID: {rule.key}</span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-2xl font-bold text-neon-purple">{rule.xp_value}</p>
          <p className="text-xs text-zinc-500">XP</p>
        </div>
      </div>
      
      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-zinc-800">
        <Button
          onClick={() => onToggle(rule)}
          variant="ghost"
          size="sm"
          className={rule.is_active ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}
          data-testid={`toggle-rule-${rule.key}`}
        >
          {rule.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        </Button>
        <Button
          onClick={() => onEdit(rule)}
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white"
          data-testid={`edit-rule-${rule.key}`}
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        {!rule.is_system && (
          <Button
            onClick={() => onDelete(rule)}
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300"
            data-testid={`delete-rule-${rule.key}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// Componente principal
export const XPRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/admin/xp-rules`);
      setRules(res.data);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
      toast.error('Erro ao carregar regras de XP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleSaveRule = async (formData) => {
    if (editingRule) {
      await axios.patch(`${API}/admin/xp-rules/${editingRule.id}`, formData);
      toast.success('Regra atualizada com sucesso!');
    } else {
      await axios.post(`${API}/admin/xp-rules`, formData);
      toast.success('Regra criada com sucesso!');
    }
    fetchRules();
  };

  const handleToggleRule = async (rule) => {
    try {
      await axios.patch(`${API}/admin/xp-rules/${rule.id}`, { is_active: !rule.is_active });
      toast.success(rule.is_active ? 'Regra desativada' : 'Regra ativada');
      fetchRules();
    } catch (error) {
      toast.error('Erro ao alterar status da regra');
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/xp-rules/${ruleToDelete.id}`);
      toast.success('Regra excluída com sucesso!');
      setRuleToDelete(null);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir regra');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Tem certeza que deseja restaurar todas as regras do sistema para os valores padrão?')) return;
    
    try {
      await axios.post(`${API}/admin/xp-rules/reset-defaults`);
      toast.success('Regras restauradas para valores padrão!');
      fetchRules();
    } catch (error) {
      toast.error('Erro ao restaurar regras');
    }
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  // Filtrar regras por categoria
  const filteredRules = filterCategory 
    ? rules.filter(r => r.category === filterCategory)
    : rules;

  // Agrupar regras por categoria para resumo
  const rulesByCategory = rules.reduce((acc, rule) => {
    const cat = rule.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(rule);
    return acc;
  }, {});

  // Calcular estatísticas
  const activeRules = rules.filter(r => r.is_active).length;
  const totalXP = rules.filter(r => r.is_active && r.category !== 'system').reduce((sum, r) => sum + r.xp_value, 0);

  return (
    <div className="space-y-6" data-testid="xp-rules-manager">
      {/* Cards de resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs">Total de Regras</span>
          </div>
          <p className="text-2xl font-bold text-white">{rules.length}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xs">Regras Ativas</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{activeRules}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-neon-purple mb-1">
            <Dumbbell className="w-4 h-4" />
            <span className="text-xs">XP Máx. por Treino</span>
          </div>
          <p className="text-2xl font-bold text-neon-purple">{totalXP}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-1">
            <img src="/fercoin.png" alt="FerCoin" className="w-4 h-4" />
            <span className="text-xs">XP por FerCoin</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">
            {rules.find(r => r.key === 'xp_per_fercoin')?.xp_value || 500}
          </p>
        </div>
      </div>

      {/* Painel principal */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-neon-purple" />
            <div>
              <h3 className="font-primary font-bold text-xl text-white">Regras de XP</h3>
              <p className="text-zinc-500 text-sm">Gerencie como os usuários ganham pontos</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleResetDefaults}
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white"
              data-testid="reset-defaults-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Restaurar Padrão
            </Button>
            <Button
              onClick={openCreateModal}
              className="bg-neon-purple hover:bg-neon-purple/80 text-white"
              data-testid="create-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nova Regra
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            onClick={() => setFilterCategory('')}
            variant={!filterCategory ? 'default' : 'outline'}
            size="sm"
            className={!filterCategory ? 'bg-neon-purple text-white' : 'border-zinc-700 text-zinc-400'}
          >
            Todas
          </Button>
          {['workout', 'bonus', 'system', 'community'].map(cat => (
            <Button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              variant={filterCategory === cat ? 'default' : 'outline'}
              size="sm"
              className={filterCategory === cat ? 'bg-neon-purple text-white' : 'border-zinc-700 text-zinc-400'}
            >
              {getCategoryIcon(cat)} <span className="ml-1">{getCategoryName(cat)}</span>
            </Button>
          ))}
        </div>

        {/* Lista de regras */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-neon-purple border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-zinc-500">Carregando regras...</p>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">Nenhuma regra encontrada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rules-list">
            {filteredRules.map(rule => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onEdit={openEditModal}
                onDelete={setRuleToDelete}
                onToggle={handleToggleRule}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showModal && (
          <RuleModal
            rule={editingRule}
            isEditing={!!editingRule}
            onClose={() => {
              setShowModal(false);
              setEditingRule(null);
            }}
            onSave={handleSaveRule}
          />
        )}
        {ruleToDelete && (
          <DeleteConfirmModal
            rule={ruleToDelete}
            onConfirm={handleDeleteRule}
            onCancel={() => setRuleToDelete(null)}
            isDeleting={isDeleting}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
